import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TableCell,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  TableSortLabel,
} from '@mui/material';
import { average, roundToOneDecimal, utcToLocalDate } from '../../room/utils';
import { visuallyHidden } from '@mui/utils';
import {
  CustomQuestion,
  TableRowType,
} from '@par/shared/types/custom-questions';
import { getSeenProgessValue } from '@par/shared/components/profile';
import { getAvgScoreGraphic, getScoreFromStats, getSeenCount } from './helper';
import { QuestionStats } from '@par/shared/types/question-stats';

export interface CustomQuestionStats extends CustomQuestion {
  stats: QuestionStats;
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof TableRowType;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'question',
    numeric: false,
    disablePadding: false,
    label: 'Question',
  },
  {
    id: 'answer',
    numeric: false,
    disablePadding: false,
    label: 'Answer',
  },
  {
    id: 'category',
    numeric: false,
    disablePadding: false,
    label: 'Category',
  },
  {
    id: 'par',
    numeric: false,
    disablePadding: false,
    label: 'PAR',
  },
  {
    id: 'submittedAt',
    numeric: false,
    disablePadding: false,
    label: 'Submitted At',
  },
  {
    id: 'seen',
    numeric: false,
    disablePadding: false,
    label: 'Seen',
  },
  {
    id: 'avgScore',
    numeric: false,
    disablePadding: false,
    label: 'Avg Score',
  },
  {
    id: null,
    numeric: false,
    disablePadding: false,
    label: '',
  },
];

type Order = 'asc' | 'desc';

interface EnhancedTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof TableRowType
  ) => void;
  order: Order;
  orderBy: string;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof TableRowType) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

type PropTypes = {
  customQuestions: CustomQuestionStats[];
  handleDetailsClick: (question: TableRowType) => void;
  handleEditClick: (question: CustomQuestionStats) => void;
  questionStatus: 'approved' | 'rejected' | 'pending' | 'disabled';
};

export const CQTable = ({
  customQuestions,
  handleDetailsClick,
  handleEditClick,
  questionStatus,
}: PropTypes) => {
  const theme = useTheme();
  const [tableRows, setTableRows] = useState<TableRowType[]>();
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof TableRowType>('submittedAt');

  useEffect(() => {
    const approvedQuestions = customQuestions.filter((q) => !!q.approved);
    const data = createCustomQuestionsTable(approvedQuestions);
    setTableRows(data);
  }, [customQuestions]);

  const createCustomQuestionsTable = (data: CustomQuestionStats[]) => {
    const rows: TableRowType[] = [];
    for (const row of data) {
      const obj: TableRowType = {
        _id: row._id,
        question: row.question,
        answer: row.answer,
        category: row.category,
        par: row.par,
        submittedAt: row.createdAt,
        seen: getSeenCount(row.stats),
        avgScore: getScoreFromStats(row),
      };
      rows.push(obj);
    }
    return rows;
  };

  const getSeenCountProgressBar = (
    tableRows: TableRowType[],
    seencount: number
  ) => {
    if (seencount === undefined || seencount === null) {
      return '--';
    }
    return (
      <>
        {seencount}
        <LinearProgress
          variant="determinate"
          value={getSeenProgessValue(tableRows, seencount)}
        />
      </>
    );
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof TableRowType
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <>
      {questionStatus === 'approved' ? (
        <TableContainer component={Paper}>
          <Table aria-label="simple table" size="small">
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {tableRows?.sort(getComparator(order, orderBy)).map((r) => (
                <>
                  <TableRow
                    hover
                    key={r._id}
                    // sx={{ '& > *': { borderBottom: 'unset' } }}
                  >
                    <TableCell width={'50%'}>
                      <Typography>{r.question}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{r.answer}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{r.category}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{r.par}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{utcToLocalDate(r.submittedAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      {getSeenCountProgressBar(tableRows, r.seen)}
                    </TableCell>
                    <TableCell>
                      {getAvgScoreGraphic(theme, r.avgScore, r.par)}
                    </TableCell>
                    <TableCell>
                      <Button onClick={() => handleDetailsClick(r)}>
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                  {/* <TableRow hover key={r._id}>
                <TableCell
                  style={{
                    paddingBottom: 0,
                    paddingTop: 0,
                  }}
                  colSpan={6}
                >
                  <Stack
                    direction={'row'}
                    sx={{
                      display: 'flex',
                      justifyContent: 'right',
                    }}
                  >
                    <Stack
                      direction={'row'}
                      spacing={2}
                      sx={{
                        paddingX: 2,
                        border: '1px solid gray',
                      }}
                    >
                      <Typography>
                        Submitted: {utcToLocalDate(r.submittedAt)}
                      </Typography>
                      <Divider orientation="vertical" flexItem />
                      <Typography>
                        Seen: {getSeenCountProgressBar(tableRows, r.seen)}
                      </Typography>
                      <Divider orientation="vertical" flexItem />
                      <Typography>
                        Avg: {getAvgScoreGraphic(r.avgScore, r.par)}
                      </Typography>
                    </Stack>
                  </Stack>
                </TableCell>
              </TableRow> */}
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper}>
          <Table aria-label="simple table" size="small">
            <TableHead>
              <TableRow>
                <TableCell width={'40%'}>Question</TableCell>
                <TableCell width={'10%'}>Answer</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>PAR</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Review Status</TableCell>
                <TableCell>Reason for Rejection</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customQuestions?.map((q) => (
                <TableRow hover key={q._id}>
                  <TableCell width={'40%'}>
                    <Typography>{q.question}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{q.answer}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{q.category}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{q.par}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{utcToLocalDate(q.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        color:
                          q.approved === null
                            ? 'Pending'
                            : q.approved
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                      }}
                    >
                      {' '}
                      {q.approved === null
                        ? 'Pending'
                        : q.approved
                        ? 'Approved'
                        : 'Rejected'}
                    </Typography>
                  </TableCell>
                  <TableCell>{q.rejectionComment}</TableCell>
                  <TableCell>
                    {q.approved === null ? (
                      <Button onClick={() => handleEditClick(q)}>Edit</Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
